import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  EyeOff,
  Database,
  KeyRound,
  Mail,
  Clock,
  Ghost,
  Cookie,
  Globe2,
  Users,
  UserX,
  ScrollText,
  Landmark,
  CreditCard,
} from "lucide-react";

const LAST_UPDATED = "4 August 2026";

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy policy · Phantom Industries";
  }, []);

  return (
    <main className="min-h-screen bg-black text-white pt-24" data-testid="privacy-page">
      <section className="relative">
        <div className="absolute inset-0 aurora-purple opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 py-14">
          <div className="chip"><Ghost className="h-3 w-3" /> Privacy policy</div>
          <h1 className="mt-4 font-headline text-5xl md:text-6xl font-bold leading-[0.95] text-gradient-purple">
            We don't track you.<br />We don't hold what we don't need.
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            This policy is written in plain English. It explains exactly what we
            collect, why we collect it, how long we keep it, and what your rights
            are — under the Australian <span className="text-white/80">Privacy Act 1988 (Cth)</span> and
            the thirteen Australian Privacy Principles (APPs).
          </p>
          <div className="mt-4 text-xs text-white/40">Last updated: {LAST_UPDATED}</div>
        </div>
      </section>

      <section className="pb-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="card-glass rounded-2xl p-6 border-purple-500/20">
            <div className="text-[11px] uppercase tracking-[0.2em] text-purple-300 mb-3">
              The short version
            </div>
            <ul className="text-sm text-white/80 leading-relaxed space-y-2 list-disc list-inside marker:text-purple-400">
              <li>No Google Analytics, no Meta Pixel, no ad cookies, no browser fingerprinting.</li>
              <li>The only personal data we hold is what you type into a checkout, contact, or waitlist form.</li>
              <li>Personal details are wiped 30 days after your device is delivered.</li>
              <li>We never sell, rent, or share your data with data brokers or ad networks.</li>
              <li>You can request a copy of, correction to, or deletion of your data any time.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 space-y-6">

          <Card icon={Landmark} title="1. Who we are.">
            Phantom Industries (the "we", "us" or "our" in this policy) is an
            Australian small business that sells GrapheneOS-installed hardware
            and privacy-respecting accessories. This policy covers the website{" "}
            <span className="text-white/90">phantomindustries.co</span>, its
            preview / staging domains, and any order fulfilment or customer
            support we do off the back of it.
            {" "}For a data-related request use the address on our{" "}
            <Link to="/contact" className="text-purple-300 underline underline-offset-4 hover:text-white">
              Contact
            </Link>{" "}
            page, or PGP-encrypted email if the request is sensitive
            (public key linked from that page).
          </Card>

          <Card icon={EyeOff} title="2. No analytics. No trackers. No advertising cookies.">
            <p>
              This website does not run Google Analytics, Meta Pixel, TikTok
              Pixel, LinkedIn Insight, Cloudflare Web Analytics, Plausible,
              PostHog, Hotjar, or any similar tracker. We do not set
              advertising cookies. We do not fingerprint your browser.
              If your ad-blocker shows an empty list on this page, it's working —
              because there is nothing to block.
            </p>
            <p className="mt-3">
              We do keep short-lived server access logs (IP address, user
              agent, request path, timestamp) at our hosting provider for the
              purpose of blocking abuse and diagnosing outages. These logs
              rotate out inside 14 days.
            </p>
          </Card>

          <Card icon={Database} title="3. What personal information we collect.">
            <p>We only collect information you deliberately give us. Specifically:</p>
            <ul className="mt-3 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>
                <span className="text-white/90">Checkout data</span> — full name,
                email, optional phone number, shipping address, and any notes
                you attach to your order. Collected only if you complete an order.
              </li>
              <li>
                <span className="text-white/90">Order + payment metadata</span> —
                what you ordered, the tier, the total, the AUD price at the
                moment of purchase, the order reference, and (for crypto) the
                wallet address shown to you plus the amount quoted at the
                locked-in exchange rate.
              </li>
              <li>
                <span className="text-white/90">Card payment data</span> — if
                you pay by card, checkout is hosted by Stripe. We never see or
                store your card number, CVC, or expiry. Stripe returns to us
                only a payment status, an internal session ID, and a payment
                intent ID that we can use to issue refunds. Stripe's own
                privacy notice governs their handling of the card data.
              </li>
              <li>
                <span className="text-white/90">Contact-form messages</span> —
                the name, email, subject and message you submit through the
                Contact page. Retained only for the duration of the
                conversation, then archived and deleted.
              </li>
              <li>
                <span className="text-white/90">Phantom X waitlist entry</span> —
                the contact handle you provide (Signal number, Telegram
                username, email, or Session ID), the platform you want a build
                for, and an optional note. Used solely to notify you when the
                app is ready.
              </li>
              <li>
                <span className="text-white/90">Nothing else.</span> No date of
                birth, no government ID, no social handles beyond what you
                voluntarily enter, no browsing history from other sites.
              </li>
            </ul>
          </Card>

          <Card icon={KeyRound} title="4. How we use your information.">
            <ul className="mt-2 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>Building, testing, and shipping your device.</li>
              <li>Contacting you about your order (dispatch, delays, warranty).</li>
              <li>Verifying that a payment has cleared (on-chain for crypto, via Stripe or your bank for card / bank transfer).</li>
              <li>Answering messages you send us.</li>
              <li>Emailing you when Phantom X launches — but only if you joined the waitlist.</li>
              <li>Detecting and blocking fraud or abuse of our checkout.</li>
              <li>Meeting our legal obligations (tax, consumer guarantees under the Australian Consumer Law, lawful requests).</li>
            </ul>
            <p className="mt-3">
              We do <em>not</em> use your data to profile you, personalise ads,
              train models, or sell you upgrades you didn't ask about.
            </p>
          </Card>

          <Card icon={Cookie} title="5. Cookies and local storage.">
            <p>
              We use one first-party <span className="text-white/90">browser
              localStorage</span> entry to remember what you put in your cart
              between page reloads. It never leaves your device. We use short-lived
              first-party cookies only for essential state (session hand-off
              between the shop and the checkout). We do not use any third-party
              cookies, and we do not need a cookie banner because we set no
              non-essential cookies.
            </p>
          </Card>

          <Card icon={Users} title="6. Who we share your data with.">
            <p>We share information only with the parties strictly required to fulfil your order:</p>
            <ul className="mt-3 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>
                <span className="text-white/90">Courier</span> — receives your
                name and shipping address for that shipment only. Nothing else.
              </li>
              <li>
                <span className="text-white/90">Stripe</span> — if you pay by
                card, Stripe processes the card details directly; we receive a
                status back but never see the card itself. Stripe is a global
                processor; card data may be handled on servers outside Australia
                (typically the United States and Europe).
              </li>
              <li>
                <span className="text-white/90">Our bank</span> — if you pay by
                PayID / OSKO / bank transfer, your bank naturally sees the
                transfer; we see only the amount and the reference on our end.
              </li>
              <li>
                <span className="text-white/90">CoinGecko</span> — we call
                CoinGecko's public price API to display the current AUD price
                for XMR / LTC. This call is server-to-server and contains no
                information about you.
              </li>
              <li>
                <span className="text-white/90">Our hosting + database
                provider</span> — the servers that store the order records
                you've entered. Bound by data-processing agreements. No
                marketing access to your data.
              </li>
            </ul>
            <p className="mt-3">
              We do not sell, rent, or share your data with data brokers,
              advertising networks, "analytics" companies, or any third party
              not listed above.
            </p>
          </Card>

          <Card icon={Globe2} title="7. Cross-border data transfers.">
            <p>
              Our own database is hosted with an Australian-region cloud
              provider. When you pay by card, Stripe may process that payment
              on servers overseas (typically the US and EU). Under APP 8, we
              take reasonable steps to ensure any overseas recipient handles
              your personal data consistently with the APPs — for Stripe,
              this is governed by Stripe's binding data-processing terms and
              their compliance with GDPR / CCPA / equivalent frameworks.
            </p>
          </Card>

          <Card icon={CreditCard} title="8. Payment-specific notes.">
            <ul className="mt-2 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>
                <span className="text-white/90">Card (Stripe)</span> — Stripe
                receives your card number, name on card, expiry, CVC, and
                billing address. Governed by{" "}
                <a
                  href="https://stripe.com/au/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-purple-300 underline underline-offset-4 hover:text-white"
                >
                  Stripe's privacy notice
                </a>.
              </li>
              <li>
                <span className="text-white/90">Monero / Litecoin</span> — we
                display a wallet address and an AUD-locked amount. Once you
                send, we verify the transaction on-chain. We do not require or
                store the wallet you sent from. Note that Litecoin's base chain
                is public; XMR is private by default.
              </li>
              <li>
                <span className="text-white/90">Bank transfer / PayID</span> —
                your bank details are visible to our bank as part of any
                standard transfer. We store only the reference and the amount.
              </li>
            </ul>
          </Card>

          <Card icon={Clock} title="9. How long we retain data.">
            <ul className="mt-2 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>
                <span className="text-white/90">Personal contact details</span> —
                automatically purged 30 days after successful delivery.
              </li>
              <li>
                <span className="text-white/90">Order record</span> — retained
                for 7 years in a de-identified form (product, tier, total,
                reference, timestamp) as required for tax and Consumer Law
                compliance. Your name / address / email is scrubbed from that
                record on the 30-day mark.
              </li>
              <li>
                <span className="text-white/90">Contact-form messages</span> —
                deleted once the conversation is closed, at most 90 days.
              </li>
              <li>
                <span className="text-white/90">Waitlist entry</span> — kept
                until Phantom X launches and we've notified you, then deleted.
                You can unsubscribe at any time by messaging us.
              </li>
              <li>
                <span className="text-white/90">Server access logs</span> —
                rotate out inside 14 days.
              </li>
            </ul>
            <p className="mt-3">
              If you ask us to delete your data sooner, we will — email us from
              the same address you ordered with and we'll action the request
              inside 7 days (see section 12).
            </p>
          </Card>

          <Card icon={ShieldCheck} title="10. How we keep your data safe.">
            <ul className="mt-2 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>All traffic to this website is over TLS (HTTPS). No plain-HTTP endpoint exists.</li>
              <li>The database is not exposed to the public internet; access requires an authenticated internal token.</li>
              <li>The admin dashboard uses a token-based auth header, not username / password. That token is rotated when compromised.</li>
              <li>We minimise what we collect in the first place, on the principle that data we never held cannot be leaked or subpoenaed.</li>
              <li>Backups are encrypted at rest.</li>
            </ul>
          </Card>

          <Card icon={ScrollText} title="11. Law enforcement requests.">
            <p>
              We comply with lawful Australian orders (valid subpoena, notice
              to produce, or court order) the same way any Australian business
              does. In practice, our data-minimisation posture means we
              typically do not hold the material sought — once your 30-day
              warranty window has closed, we no longer have your delivery
              details, and we never had your card number in the first place.
            </p>
            <p className="mt-3">
              Where the law permits it, we will notify affected customers of a
              request that concerns their data, so you have the chance to
              respond.
            </p>
          </Card>

          <Card icon={Mail} title="12. Your rights.">
            <p>Under the Privacy Act 1988 and the APPs, you have the right to:</p>
            <ul className="mt-3 space-y-2 list-disc list-inside marker:text-purple-400">
              <li>Request a copy of the personal information we hold about you (APP 12).</li>
              <li>Ask us to correct anything that's out-of-date or inaccurate (APP 13).</li>
              <li>Ask us to delete your data ahead of the 30-day auto-purge.</li>
              <li>Withdraw consent for any future contact (e.g. waitlist emails).</li>
              <li>Complain to us directly, and to the Office of the Australian Information Commissioner (OAIC) if you're not satisfied with our response.</li>
            </ul>
            <p className="mt-3">
              Email your request from the same address you ordered with (so we
              can verify it's you) and we'll respond within 7 days. Requests to
              PGP-encrypted addresses are welcome.
            </p>
          </Card>

          <Card icon={UserX} title="13. Children's privacy.">
            Our products and services are aimed at adults. We do not knowingly
            sell to or collect data from anyone under 18. If you believe a
            child has provided us with personal information, contact us and
            we'll delete it immediately.
          </Card>

          <Card icon={ScrollText} title="14. Changes to this policy.">
            When we update this policy we'll change the "Last updated" date at
            the top of the page. Material changes (new third-party
            recipients, new categories of data, new retention periods, changes
            of legal jurisdiction) will also be flagged with a notice on our
            homepage for at least 30 days.
          </Card>

          <Card icon={Landmark} title="15. Complaints.">
            If you think we've handled your data poorly, please tell us first
            via the <Link to="/contact" className="text-purple-300 underline underline-offset-4 hover:text-white">Contact</Link> page — we take complaints
            seriously and we'll respond within 30 days. If you're not
            satisfied with our response, you can escalate to the{" "}
            <a
              href="https://www.oaic.gov.au/privacy/privacy-complaints"
              target="_blank"
              rel="noreferrer"
              className="text-purple-300 underline underline-offset-4 hover:text-white"
            >
              Office of the Australian Information Commissioner (OAIC)
            </a>.
          </Card>

          <div className="mt-10 text-sm text-white/50 text-center">
            Questions? <Link to="/contact" className="text-purple-300 hover:text-white underline underline-offset-4">Contact us</Link>.
          </div>
        </div>
      </section>
    </main>
  );
};

const Card = ({ icon: Icon, title, children }) => (
  <div className="card-glass rounded-2xl p-6">
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
        <Icon className="h-4 w-4 text-purple-300" />
      </div>
      <div className="font-display font-semibold text-white">{title}</div>
    </div>
    <div className="mt-3 text-sm text-white/70 leading-relaxed">{children}</div>
  </div>
);

export default Privacy;
